import { Injectable } from '@angular/core';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

export interface PdfConfig {
  reportTitle?: string;
  reportInfo?: { label: string; value: string }[];
  headers?: string[];
  tableData?: any[][];
  details?: { label: string; value: string }[];
  type?: 'financial' | 'service-order' | 'custom';
  orderDetails?: { label: string; value: any }[];
  serviceData?: any;
  customData?: any;
}

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  // Constantes para controle de texto
  private readonly MAX_COMMENT_LENGTH = 5000;
  private readonly MIN_PAGE_SPACE = 200;

  // Método principal para gerar um PDF estilizado
  async generateStyledPDF(config: PdfConfig): Promise<Blob> {
    switch (config.type) {
      case 'financial':
        return this.generateFinancialReport(config);
      case 'service-order':
        return this.generateServiceOrderReport(config);
      default:
        return this.generateCustomReport(config);
    }
  }

  // Método para gerar relatório financeiro
  private async generateFinancialReport(config: PdfConfig): Promise<Blob> {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Configurações de página
    const pageWidth = 595;
    const pageHeight = 842;
    const margin = 50;
    const textFontSize = 9;
    const headerFontSize = 16;
    const titleFontSize = 14;
    
    // Configurações da tabela
    const tableStartY = pageHeight - margin - 180; // Mais espaço para gráficos
    const rowHeight = 20;
    const headerHeight = 25;
    
    // Área reservada para o rodapé
    const footerHeight = 80;
    const footerY = 40;
    const availableTableHeight = tableStartY - margin - footerHeight;
    const maxRowsPerPage = Math.max(Math.floor(availableTableHeight / rowHeight), 6);

    // Validar dados da tabela
    const validatedHeaders = (config.headers || []).map((header: any) => String(header || ''));
    const validatedTableData = (config.tableData || []).map((row: any) => 
      row.map((value: any) => String(value || ''))
    );

    // Calcular largura das colunas baseada no conteúdo
    const columnWidths = this.calculateFinancialColumnWidths(validatedHeaders.length, pageWidth - 2 * margin);

    let currentPage = 0;
    let currentRowIndex = 0;

    while (currentRowIndex < validatedTableData.length) {
      // Adicionar nova página
      pdfDoc.addPage([pageWidth, pageHeight]);
      const page = pdfDoc.getPage(currentPage);
      let currentY = pageHeight - margin;

      // === Cabeçalho com imagem (apenas na primeira página) ===
      if (currentPage === 0) {
        await this.drawHeader(page, pdfDoc, boldFont, font, margin, pageWidth, currentY);
        currentY -= 80;

        // === Título do Relatório ===
        if (config.reportTitle) {
          page.drawText(config.reportTitle, {
            x: margin,
            y: currentY,
            size: titleFontSize,
            font: boldFont,
            color: rgb(0.1, 0.1, 0.1),
          });
          currentY -= 25;
        }

        // === Informações do Relatório ===
        if (config.reportInfo) {
          config.reportInfo.forEach((info: { label: string; value: string }) => {
            page.drawText(`${info.label}: ${info.value}`, {
              x: margin,
              y: currentY,
              size: textFontSize,
              font,
              color: rgb(0, 0, 0),
            });
            currentY -= 18;
          });
          currentY -= 10;
        }

        // === Gráfico de Pizza Simples ===
        currentY = this.drawSimplePieChart(page, config, margin, currentY, pageWidth, font, boldFont);
        currentY -= 20;
      } else {
        currentY = pageHeight - margin - 20;
      }

      // === Renderizar Tabela ===
      if (validatedHeaders.length > 0 && validatedTableData.length > 0) {
        // Cabeçalho da tabela (apenas na primeira página)
        if (currentPage === 0) {
          this.drawFinancialTableHeader(page, validatedHeaders, margin, currentY, columnWidths, headerHeight, boldFont, textFontSize);
          currentY -= headerHeight + 5;
        }

        // Calcular quantas linhas cabem nesta página
        let rowsForThisPage = Math.min(
          maxRowsPerPage,
          validatedTableData.length - currentRowIndex
        );
        
        const availableHeight = currentY - footerHeight - 20;
        const maxRowsByHeight = Math.floor(availableHeight / rowHeight);
        rowsForThisPage = Math.min(rowsForThisPage, maxRowsByHeight);

        // Dados da tabela para esta página
        for (let i = 0; i < rowsForThisPage; i++) {
          const rowIndex = currentRowIndex + i;
          const row = validatedTableData[rowIndex];

          if (currentY - rowHeight < footerHeight + 20) {
            break;
          }

          // Linha zebrada
          if (rowIndex % 2 === 0) {
            page.drawRectangle({
              x: margin,
              y: currentY - rowHeight,
              width: pageWidth - 2 * margin,
              height: rowHeight,
              color: rgb(0.98, 0.98, 0.98),
            });
          }

          // Bordas da linha
          page.drawRectangle({
            x: margin,
            y: currentY - rowHeight,
            width: pageWidth - 2 * margin,
            height: rowHeight,
            borderColor: rgb(0.8, 0.8, 0.8),
            borderWidth: 0.5,
          });

          // Linhas divisórias entre colunas
          let dividerX = margin;
          for (let i = 1; i < columnWidths.length; i++) {
            dividerX += columnWidths[i - 1];
            page.drawLine({
              start: { x: dividerX, y: currentY },
              end: { x: dividerX, y: currentY - rowHeight },
              thickness: 0.5,
              color: rgb(0.8, 0.8, 0.8),
            });
          }

          // Renderizar células
          row.forEach((value: string, colIndex: number) => {
            let cellX = margin;
            for (let i = 0; i < colIndex; i++) {
              cellX += columnWidths[i];
            }
            
            const maxWidth = columnWidths[colIndex] * 0.90;
            const fontSize = textFontSize - 1;
            const cellFont = colIndex === 0 ? boldFont : font;
            
            // Cor especial para valores monetários
            let textColor = rgb(0, 0, 0);
            if (colIndex === 4 && value.includes('R$')) { // Coluna de valor
              if (value.includes('-')) {
                textColor = rgb(0.8, 0.2, 0.2); // Vermelho para valores negativos
                } else {
                textColor = rgb(0.2, 0.6, 0.2); // Verde para valores positivos
              }
            }
            
            const textLines = this.breakTextIntoLines(value, maxWidth, cellFont, fontSize);
            const textMargin = columnWidths[colIndex] * 0.05;
            const lineHeight = fontSize + 2;
            
            textLines.forEach((line, lineIndex) => {
              const lineY = currentY - rowHeight + 7 + (lineIndex * lineHeight);
              
              if (lineY < currentY - rowHeight + rowHeight - 5) {
                page.drawText(line, {
                  x: cellX + textMargin,
                  y: lineY,
                  size: fontSize,
                  font: cellFont,
                  color: textColor,
                });
              }
            });
          });

          currentY -= rowHeight;
        }

        currentRowIndex += rowsForThisPage;
      }

      // === Rodapé ===
      this.drawFooter(page, margin, pageWidth, footerY, boldFont, font, textFontSize);

      currentPage++;
    }

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: 'application/pdf' });
  }

  // Método para gerar relatório de ordem de serviço (mantém compatibilidade)
  private async generateServiceOrderReport(config: PdfConfig): Promise<Blob> {
    // Usar o método existente para ordens de serviço
    return this.generateStyledPDFLegacy(config.orderDetails || [], config.serviceData || {});
  }

  // Método para gerar relatório customizado
  private async generateCustomReport(config: PdfConfig): Promise<Blob> {
    // Implementação similar ao relatório financeiro mas com dados customizados
    return this.generateFinancialReport(config);
  }

  // Método legado para ordens de serviço (mantém compatibilidade)
  private async generateStyledPDFLegacy(
    orderDetails: { label: string; value: any }[],
    serviceData: any
  ): Promise<Blob> {
    // Aqui você pode copiar toda a lógica existente do método generateStyledPDF original
    // Por simplicidade, vou criar uma versão simplificada
    const pdfDoc = await PDFDocument.create();
    let page = pdfDoc.addPage([595, 842]);
    const { width, height } = page.getSize();
    
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const margin = 40;
    const textSize = 9;
    const titleSize = 10;
    const headerSize = 14;
    let currentY = height - margin;

    const primaryColor = rgb(0.176, 0.176, 0.514);
    const secondaryColor = rgb(0.95, 0.95, 0.95);
    const textColor = rgb(0.15, 0.15, 0.15);
    const borderColor = rgb(0.75, 0.75, 0.75);
    const accentColor = rgb(0.2, 0.6, 0.8);
    
    // === CABEÇALHO ===
    await this.drawHeader(page, pdfDoc, helveticaBold, helveticaFont, margin, width, currentY, primaryColor, secondaryColor);
    currentY -= 90;
    
    // === TÍTULO DA ORDEM DE SERVIÇO ===
    const orderCode = serviceData.code || '';
    const orderTitle = `ORDEM DE SERVIÇO ${orderCode ? `Nº ${orderCode}` : ''}`;
    this.drawText(page, orderTitle, width/2, currentY, headerSize, helveticaBold, primaryColor, true);
    currentY -= 25;
    
    // === TÍTULO DO SERVIÇO ===
    if (serviceData.title && serviceData.title !== 'Não informado') {
      const titleText = serviceData.title.toUpperCase();
      this.drawText(page, titleText, width/2, currentY - 8, titleSize + 2, helveticaBold, primaryColor, true);
      currentY -= 35;
    }
    
    // === SEÇÃO: INFORMAÇÕES DO CLIENTE ===
    this.drawSectionTitle(page, "INFORMAÇÕES DO CLIENTE", margin, currentY, helveticaBold, primaryColor, accentColor);
    currentY -= 25;

    const clientName = orderDetails.find(d => d.label === "Cliente")?.value || serviceData.customers || serviceData.customer || "Não informado";
    const partnerValue = orderDetails.find(d => d.label === "Parceiro")?.value || "Não informado";
    const responsibleValue = serviceData.responsible || orderDetails.find(d => d.label === "Responsável")?.value || "Não informado";
    
    const clientInfoFields = [
      { label: "Cliente:", value: clientName },
      { label: "Parceiro:", value: partnerValue },
      { label: "Responsável:", value: responsibleValue }
    ];
    
    currentY = this.drawFieldTable(page, clientInfoFields, margin, currentY, width - 2*margin, helveticaBold, helveticaFont, textColor, textSize, borderColor, accentColor, false);
    currentY -= 20;

    // === SEÇÃO: DETALHES DO SERVIÇO ===
    this.drawSectionTitle(page, "DETALHES DO SERVIÇO", margin, currentY, helveticaBold, primaryColor, accentColor);
    currentY -= 25;
    
    const serviceInfoFields = [
      { label: "Sistema:", value: serviceData.system || "Não informado" },
      { label: "Tipo:", value: serviceData.type || "Não informado" },
      { label: "Módulo:", value: serviceData.module || "Não informado" },
      { label: "Prioridade:", value: serviceData.priority || "Não informado" },
      { label: "Data Estimada:", value: serviceData.estimation || "EM ANÁLISE" },
      { label: "Custo:", value: serviceData.cost || "Não informado" }
    ];
    
    currentY = this.drawFieldTable(page, serviceInfoFields, margin, currentY, width - 2*margin, helveticaBold, helveticaFont, textColor, textSize, borderColor, accentColor, false);
    currentY -= 20;
    
    // === SEÇÃO: DESCRIÇÃO DO SERVIÇO ===
    this.drawSectionTitle(page, "DESCRIÇÃO DO SERVIÇO", margin, currentY, helveticaBold, primaryColor, accentColor);
    currentY -= 25;
    
    const description = orderDetails.find(d => d.label === "Descrição")?.value || "Não informado";
    const processedDescription = this.processTextForPdf(description);
    
    const descriptionResult = this.drawMultilineTextAdvanced(
      page, 
      pdfDoc, 
      processedDescription, 
      margin, 
      currentY, 
      width - 2*margin, 
      helveticaFont, 
      textSize, 
      textColor, 
      15, 
      secondaryColor, 
      borderColor, 
      false,
      orderCode
    );
    
    page = descriptionResult.page;
    currentY = descriptionResult.currentY - 20;
    
    // === SEÇÃO: RESOLUÇÃO (se existir) ===
    const resolution = orderDetails.find(d => d.label === "Resolução")?.value;
    if (resolution && resolution !== "Não informado") {
      if (currentY < this.MIN_PAGE_SPACE) {
        page = pdfDoc.addPage([595, 842]);
        currentY = height - margin - 50;
        
        this.drawText(page, `ORDEM DE SERVIÇO ${orderCode ? `Nº ${orderCode}` : ''} (CONTINUAÇÃO)`, width/2, currentY, titleSize, helveticaBold, primaryColor, true);
        currentY -= 30;
      }
      
      this.drawSectionTitle(page, "RESOLUÇÃO", margin, currentY, helveticaBold, primaryColor, accentColor);
      currentY -= 25;
      
      const processedResolution = this.processTextForPdf(resolution);
      
      const resolutionResult = this.drawMultilineTextAdvanced(
        page, 
        pdfDoc, 
        processedResolution, 
        margin, 
        currentY, 
        width - 2*margin, 
        helveticaFont, 
        textSize, 
        textColor, 
        15, 
        secondaryColor, 
        borderColor, 
        false,
        orderCode
      );
      
      page = resolutionResult.page;
      currentY = resolutionResult.currentY - 20;
    }
    
    // === RODAPÉ ===
    this.drawFooter(page, margin, width, 60, helveticaBold, helveticaFont, 8);

    const pdfBytes = await pdfDoc.save();
    return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
  }

  // Método específico para relatório de contas recebidas
  async generateAccountsReceivableReport(selectedItems: any[]): Promise<Blob> {
    console.log('🔍 [PDF] Gerando relatório para', selectedItems.length, 'itens');
    
    // Log dos primeiros itens para debug
    if (selectedItems.length > 0) {
      console.log('🔍 [PDF] Primeiro item:', selectedItems[0]);
      console.log('🔍 [PDF] Campos do primeiro item:', {
        externalCode: selectedItems[0].externalCode,
        customerExternalCode: selectedItems[0].customer?.externalCode,
        customer: selectedItems[0].customer,
        customerName: selectedItems[0].customerName,
        tradeName: selectedItems[0].tradeName,
        vencimento: selectedItems[0].vencimento,
        dueDate: selectedItems[0].dueDate,
        due_date: selectedItems[0].due_date
      });
    }

    const reportData = {
      details: []
    };

    const reportConfig = {
      reportTitle: 'CLIENTES PARA LIBERAÇÃO',
      reportInfo: [
        { label: 'Data de Geração', value: new Date().toLocaleDateString('pt-BR') },
        { label: 'Hora de Geração', value: new Date().toLocaleTimeString('pt-BR') },
        { label: 'Total de Registros', value: selectedItems.length.toString() }
      ],
      headers: ['Código Externo', 'Nome do Cliente', 'Nome Fantasia'],
      tableData: selectedItems.map(item => {
        // Extrair e validar o código externo
        let externalCode = 'N/A';
        if (item.externalCode !== null && item.externalCode !== undefined) {
          externalCode = this.convertToString(item.externalCode);
        } else if (item.customer?.externalCode !== null && item.customer?.externalCode !== undefined) {
          externalCode = this.convertToString(item.customer.externalCode);
        }
        
        // Extrair e validar o nome do cliente (apenas company_name ou name)
        let customerName = 'N/A';
        if (item.customer !== null && item.customer !== undefined) {
          customerName = this.convertToString(item.customer);
        } else if (item.customerName !== null && item.customerName !== undefined) {
          customerName = this.convertToString(item.customerName);
        }
        
        // Extrair e validar o nome fantasia (campo separado)
        let tradeName = '';
        if (item.tradeName !== null && item.tradeName !== undefined && item.tradeName !== '') {
          tradeName = this.convertToString(item.tradeName);
        } else if (item.customer?.tradeName !== null && item.customer?.tradeName !== undefined && item.customer?.tradeName !== '') {
          tradeName = this.convertToString(item.customer.tradeName);
        }
        
        const row = [
          externalCode,
          customerName,
          tradeName
        ];
        
        // Log da primeira linha para debug
        if (selectedItems.indexOf(item) === 0) {
          console.log('🔍 [PDF] Primeira linha do relatório:', row);
          console.log('🔍 [PDF] Dados originais do primeiro item:', {
            externalCode: item.externalCode,
            customerExternalCode: item.customer?.externalCode,
            customer: item.customer,
            customerName: item.customerName,
            tradeName: item.tradeName,
            vencimento: item.vencimento,
            dueDate: item.dueDate,
            due_date: item.due_date
          });
        }
        
        return row;
      })
    };

    console.log('🔍 [PDF] Configuração do relatório criada, gerando PDF...');
    return this.generateStyledPDF(reportConfig);
  }

  private convertToString(value: any): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    // Se já é uma string, retorna diretamente
    if (typeof value === 'string') {
      return value;
    }
    
    // Se é um número, converte para string
    if (typeof value === 'number') {
      return value.toString();
    }
    
    // Se é um objeto, tenta converter para string JSON ou usa toString
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return value.toString();
      }
    }
    
    // Para outros tipos, usa String()
    return String(value);
  }

  private formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      
      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        console.warn('⚠️ [PDF] Data inválida:', dateString);
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR');
    } catch (e) {
      console.warn('⚠️ [PDF] Erro ao formatar data:', dateString, e);
      return String(dateString);
    }
  }

  // Método para gerar a pré-visualização do PDF
  async generatePreview(config: PdfConfig): Promise<string> {
    const pdfBlob = await this.generateStyledPDF(config);
    return URL.createObjectURL(pdfBlob);
  }

  // Métodos auxiliares
  private calculateColumnWidths(columnCount: number, availableWidth: number): number[] {
    const widths: number[] = [];
    const baseWidth = availableWidth / columnCount;
    
    for (let i = 0; i < columnCount; i++) {
      widths.push(baseWidth);
    }
    
    return widths;
  }

  // Calcular larguras das colunas para relatório financeiro
  private calculateFinancialColumnWidths(columnCount: number, availableWidth: number): number[] {
    // Proporções específicas para relatório financeiro
    const proportions = [0.12, 0.20, 0.18, 0.12, 0.15, 0.12, 0.11]; // Tipo, Título, Cliente, Sistema, Valor, Vencimento, Status
    
    const widths: number[] = [];
    for (let i = 0; i < columnCount; i++) {
      const proportion = proportions[i] || (1 / columnCount);
      widths.push(availableWidth * proportion);
    }
    
    return widths;
  }

  private drawTableHeader(page: any, headers: string[], margin: number, currentY: number, columnWidths: number[], headerHeight: number, boldFont: any, fontSize: number): void {
    const pageWidth = page.getWidth();
    
    // Fundo do cabeçalho
    page.drawRectangle({
      x: margin,
      y: currentY - headerHeight,
      width: pageWidth - 2 * margin,
      height: headerHeight,
      color: rgb(0.9, 0.9, 0.9),
    });

    // Bordas do cabeçalho
    page.drawRectangle({
      x: margin,
      y: currentY - headerHeight,
      width: pageWidth - 2 * margin,
      height: headerHeight,
      borderColor: rgb(0.7, 0.7, 0.7),
      borderWidth: 1,
    });

    headers.forEach((header: string, index: number) => {
      let headerX = margin;
      for (let i = 0; i < index; i++) {
        headerX += columnWidths[i];
      }
      
      const headerMargin = columnWidths[index] * 0.05;
      
      page.drawText(header, {
        x: headerX + headerMargin,
        y: currentY - headerHeight + 8,
        size: fontSize,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
    });

    // Linhas divisórias no cabeçalho
    let headerDividerX = margin;
    for (let i = 1; i < columnWidths.length; i++) {
      headerDividerX += columnWidths[i - 1];
      page.drawLine({
        start: { x: headerDividerX, y: currentY },
        end: { x: headerDividerX, y: currentY - headerHeight },
        thickness: 0.5,
        color: rgb(0.7, 0.7, 0.7),
      });
    }
  }

  // Desenhar cabeçalho da tabela financeira
  private drawFinancialTableHeader(page: any, headers: string[], margin: number, currentY: number, columnWidths: number[], headerHeight: number, boldFont: any, fontSize: number): void {
    const pageWidth = page.getWidth();
    
    // Fundo do cabeçalho
    page.drawRectangle({
      x: margin,
      y: currentY - headerHeight,
      width: pageWidth - 2 * margin,
      height: headerHeight,
      color: rgb(0.2, 0.4, 0.6),
    });

    // Bordas do cabeçalho
    page.drawRectangle({
      x: margin,
      y: currentY - headerHeight,
      width: pageWidth - 2 * margin,
      height: headerHeight,
      borderColor: rgb(0.1, 0.3, 0.5),
      borderWidth: 1,
    });

    headers.forEach((header: string, index: number) => {
      let headerX = margin;
      for (let i = 0; i < index; i++) {
        headerX += columnWidths[i];
      }
      
      const headerMargin = columnWidths[index] * 0.05;
      
      page.drawText(header, {
        x: headerX + headerMargin,
        y: currentY - headerHeight + 8,
        size: fontSize,
        font: boldFont,
        color: rgb(1, 1, 1),
      });
    });

    // Linhas divisórias no cabeçalho
    let headerDividerX = margin;
    for (let i = 1; i < columnWidths.length; i++) {
      headerDividerX += columnWidths[i - 1];
      page.drawLine({
        start: { x: headerDividerX, y: currentY },
        end: { x: headerDividerX, y: currentY - headerHeight },
        thickness: 0.5,
        color: rgb(1, 1, 1),
      });
    }
  }

  private breakTextIntoLines(text: string, maxWidth: number, font: any, fontSize: number): string[] {
    const lines: string[] = [];
    const words = text.split(' ');
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const testWidth = font.widthOfTextAtSize(testLine, fontSize);
      
      if (testWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          let charLine = '';
          for (const char of word) {
            const testCharLine = charLine + char;
            const charWidth = font.widthOfTextAtSize(testCharLine, fontSize);
            
            if (charWidth <= maxWidth) {
              charLine = testCharLine;
            } else {
              if (charLine) {
                lines.push(charLine);
                charLine = char;
              } else {
                lines.push(char);
                break;
              }
            }
          }
          if (charLine) {
            currentLine = charLine;
          }
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }

  private drawFooter(page: any, margin: number, pageWidth: number, footerY: number, boldFont: any, font: any, fontSize: number): void {
    // Fundo do rodapé
    page.drawRectangle({
      x: margin,
      y: footerY - 10,
      width: pageWidth - 2 * margin,
      height: 40,
      color: rgb(0.95, 0.95, 0.95),
      opacity: 0.3,
    });

    // Linha superior do rodapé
    page.drawLine({
      start: { x: margin, y: footerY + 20 },
      end: { x: pageWidth - margin, y: footerY + 20 },
      thickness: 1,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Informações da empresa
    const companyText = 'MEGA TECH AUTOMAÇÃO LTDA - CNPJ: 32.859.554/0001-35';
    const companyTextWidth = boldFont.widthOfTextAtSize(companyText, fontSize - 2);
    page.drawText(companyText, {
      x: (pageWidth - companyTextWidth) / 2,
      y: footerY + 5,
      size: fontSize - 2,
      font: boldFont,
      color: rgb(0.2, 0.2, 0.2),
    });

    // Data e hora de geração
    const footerText = `Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`;
    const footerTextWidth = font.widthOfTextAtSize(footerText, fontSize - 2);
    page.drawText(footerText, {
      x: (pageWidth - footerTextWidth) / 2,
      y: footerY - 5,
      size: fontSize - 2,
      font,
      color: rgb(0.5, 0.5, 0.5),
    });
  }

  // Métodos existentes mantidos para compatibilidade
  private async drawHeader(page: any, pdfDoc: PDFDocument, boldFont: any, regularFont: any, margin: number, width: number, y: number, primaryColor?: any, secondaryColor?: any): Promise<void> {
    try {
      const imageBytes = await fetch('/assets/img/Favicon-GrupoMegatech.png').then((res) => res.arrayBuffer());
      const image = await pdfDoc.embedPng(imageBytes);
      const imageWidth = 60;
      const imageHeight = 60;
      
      page.drawImage(image, {
        x: margin,
        y: y - imageHeight,
        width: imageWidth,
        height: imageHeight,
      });
      
      this.drawText(page, 'MEGA TECH AUTOMAÇÃO LTDA', margin + imageWidth + 15, y - 15, 14, boldFont, primaryColor || rgb(0.176, 0.176, 0.514));
      this.drawText(page, 'CNPJ: 32.859.554/0001-35', margin + imageWidth + 15, y - 32, 9, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, 'Av. 24 de Agosto, N° 195 - Sala 5 - Vila Jaiara, Anápolis - GO', margin + imageWidth + 15, y - 45, 8, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, 'contato@grupomegatech.com.br', margin + imageWidth + 15, y - 58, 8, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, '(62) 3099-1844', width - margin - 80, y - 58, 8, regularFont, rgb(0.2, 0.2, 0.2));
      
      page.drawLine({
        start: { x: margin, y: y - 70 },
        end: { x: width - margin, y: y - 70 },
        thickness: 1,
        color: primaryColor || rgb(0.176, 0.176, 0.514),
      });
    } catch (error) {
      console.error('Erro ao desenhar o cabeçalho:', error);
      
      this.drawText(page, 'MEGA TECH AUTOMAÇÃO LTDA', margin, y - 15, 14, boldFont, primaryColor || rgb(0.176, 0.176, 0.514));
      this.drawText(page, 'CNPJ: 32.859.554/0001-35', margin, y - 32, 9, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, 'Av. 24 de Agosto, N° 195 - Sala 5 - Vila Jaiara, Anápolis - GO', margin, y - 45, 8, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, 'contato@grupomegatech.com.br', margin, y - 58, 8, regularFont, rgb(0.2, 0.2, 0.2));
      this.drawText(page, '(62) 3099-1844', width - margin - 80, y - 58, 8, regularFont, rgb(0.2, 0.2, 0.2));
      
      page.drawLine({
        start: { x: margin, y: y - 70 },
        end: { x: width - margin, y: y - 70 },
        thickness: 1,
        color: primaryColor || rgb(0.176, 0.176, 0.514),
      });
    }
  }
  
  private drawText(page: any, text: string, x: number, y: number, size: number, font: any, color: any, center: boolean = false): void {
    if (!text) text = "";
    
    if (center) {
      const textWidth = font.widthOfTextAtSize(text, size);
      x = x - textWidth / 2;
    }
    
    page.drawText(text, {
      x,
      y,
      size,
      font,
      color,
    });
  }
  
  private drawLine(page: any, startX: number, startY: number, endX: number, endY: number, thickness: number, color: any): void {
    page.drawLine({
      start: { x: startX, y: startY },
      end: { x: endX, y: endY },
      thickness,
      color,
    });
  }
  
  private drawSectionTitle(page: any, title: string, x: number, y: number, font: any, color: any, accentColor: any): void {
    this.drawText(page, title, x, y, 10, font, color);
    this.drawLine(page, x, y - 5, x + (page.getWidth() - 2 * x), y - 5, 0.5, accentColor);
  }
  
  private drawFieldTable(
    page: any, 
    fields: { label: string; value: string }[], 
    x: number, 
    y: number, 
    width: number, 
    boldFont: any, 
    regularFont: any, 
    textColor: any,
    textSize: number,
    borderColor: any,
    accentColor: any,
    drawBackground: boolean = true
  ): number {
    const rowHeight = 25;
    const padding = 5;
    let currentY = y;
    
    const columns = fields.length <= 3 ? fields.length : (fields.length <= 6 ? 3 : 2);
    const columnWidth = width / columns;
    
    const rows = [];
    for (let i = 0; i < fields.length; i += columns) {
      rows.push(fields.slice(i, i + columns));
    }
    
    rows.forEach((row, rowIndex) => {
      const rowY = currentY - rowHeight;
      
      this.drawLine(page, x, rowY, x + width, rowY, 0.5, borderColor);

      row.forEach((field, colIndex) => {
        const fieldX = x + colIndex * columnWidth + padding;
        
        this.drawText(page, field.label, fieldX, currentY - rowHeight/2 + 4, textSize, boldFont, accentColor);
        
        const valueX = fieldX + boldFont.widthOfTextAtSize(field.label, textSize) + 5;
        const maxValueWidth = columnWidth - boldFont.widthOfTextAtSize(field.label, textSize) - padding * 2 - 10;
        
        let value = field.value || "";
        if (regularFont.widthOfTextAtSize(value, textSize) > maxValueWidth) {
          while (regularFont.widthOfTextAtSize(value + "...", textSize) > maxValueWidth && value.length > 0) {
            value = value.slice(0, -1);
          }
          value += "...";
        }
        
        this.drawText(page, value, valueX, currentY - rowHeight/2 + 4, textSize, regularFont, textColor);
        
        if (colIndex < row.length - 1) {
          this.drawLine(
            page, 
            x + (colIndex + 1) * columnWidth, 
            currentY, 
            x + (colIndex + 1) * columnWidth, 
            rowY, 
            0.5, 
            borderColor
          );
        }
      });
      
      if (rowIndex === 0) {
        this.drawLine(page, x, currentY, x + width, currentY, 0.5, borderColor);
      }
      
      currentY -= rowHeight;
    });
    
    return currentY;
  }

  private processTextForPdf(text: string): string {
    if (!text || text === "Não informado") return "Não informado";
    
    let processedText = text.toString().trim();
    
    if (processedText.length > this.MAX_COMMENT_LENGTH) {
      processedText = processedText.substring(0, this.MAX_COMMENT_LENGTH - 3) + "...";
    }
    
    processedText = processedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    processedText = processedText.replace(/\n{3,}/g, '\n\n');
    
    return processedText;
  }

  private drawMultilineTextAdvanced(
    page: any,
    pdfDoc: PDFDocument,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    font: any,
    size: number,
    color: any,
    lineHeight: number,
    backgroundColor: any,
    borderColor: any,
    drawBackground: boolean = true,
    orderCode: string = ''
  ): { page: any; currentY: number } {
    if (!text) text = "Não informado";
    
    const margin = 40;
    const { width, height } = page.getSize();
    let currentPage = page;
    let currentY = y;
    
    const paragraphs = text.split('\n');
    let allTextLines: string[] = [];
    
    for (const paragraph of paragraphs) {
      if (paragraph.trim() === '') {
        allTextLines.push('');
        continue;
      }
      
      const words = paragraph.split(' ');
      let processedWords: string[] = [];
      
      for (const word of words) {
        if (font.widthOfTextAtSize(word, size) > maxWidth) {
          const brokenWords = this.breakLongWord(word, maxWidth, font, size);
          processedWords.push(...brokenWords);
        } else {
          processedWords.push(word);
        }
      }
      
      let line = '';
      for (const word of processedWords) {
        const testLine = line.length === 0 ? word : `${line} ${word}`;
        const testWidth = font.widthOfTextAtSize(testLine, size);
        
        if (testWidth > maxWidth && line.length > 0) {
          allTextLines.push(line);
          line = word;
        } else {
          line = testLine;
        }
      }
      
      if (line.length > 0) {
        allTextLines.push(line);
      }
    }
    
    const totalHeight = allTextLines.length * lineHeight + 20;
    const padding = 10;
    
    if (currentY - totalHeight < 100) {
      currentPage = pdfDoc.addPage([595, 842]);
      currentY = height - margin - 50;
      
      this.drawText(currentPage, `ORDEM DE SERVIÇO ${orderCode ? `Nº ${orderCode}` : ''} (CONTINUAÇÃO)`, width/2, currentY, 10, font, color, true);
      currentY -= 30;
    }
    
    if (drawBackground) {
      currentPage.drawRectangle({
        x: x - padding,
        y: currentY - totalHeight - padding,
        width: maxWidth + (padding * 2),
        height: totalHeight + (padding * 2),
        borderWidth: 0.5,
        borderColor: borderColor,
        color: backgroundColor,
        opacity: 0.5
      });
    }
    
    let lineY = currentY;
    for (const textLine of allTextLines) {
      if (lineY < 100) {
        currentPage = pdfDoc.addPage([595, 842]);
        lineY = height - margin - 50;
        
        this.drawText(currentPage, `ORDEM DE SERVIÇO ${orderCode ? `Nº ${orderCode}` : ''} (CONTINUAÇÃO)`, width/2, lineY, 10, font, color, true);
        lineY -= 30;
      }
      
      if (textLine.trim() !== '') {
        this.drawText(currentPage, textLine, x, lineY, size, font, color);
      }
      lineY -= lineHeight;
    }
    
    return {
      page: currentPage,
      currentY: lineY - padding
    };
  }

  private breakLongWord(word: string, maxWidth: number, font: any, size: number): string[] {
    const result: string[] = [];
    let currentPart = '';
    
    for (const char of word) {
      const testPart = currentPart + char;
      if (font.widthOfTextAtSize(testPart, size) > maxWidth && currentPart.length > 0) {
        result.push(currentPart);
        currentPart = char;
      } else {
        currentPart = testPart;
      }
    }
    
    if (currentPart.length > 0) {
      result.push(currentPart);
    }
    
    return result;
  }

  // Desenhar gráfico de pizza simples
  private drawSimplePieChart(page: any, config: PdfConfig, margin: number, currentY: number, pageWidth: number, font: any, boldFont: any): number {
    const chartCenterX = pageWidth / 2;
    const chartCenterY = currentY - 60;
    const chartRadius = 50; // Aumentado para melhor visualização
    
    // Extrair dados para o gráfico
    let contasAPagarTotal = 0;
    let contasAReceberTotal = 0;
    
    if (config.tableData) {
      console.log('🔍 [PDF] Dados da tabela para gráfico:', config.tableData);
      
      config.tableData.forEach((row: any[], index: number) => {
        console.log(`🔍 [PDF] Linha ${index}:`, row);
        
        if (row[0] === 'Contas a Pagar' && row[4]) {
          // Extrair valor da string formatada (ex: "R$ 510,00")
          const valueStr = row[4].toString();
          const cleanValue = valueStr.replace(/[^\d,.-]/g, '').replace(',', '.');
          const value = parseFloat(cleanValue);
          
          console.log(`🔍 [PDF] Contas a Pagar - Original: "${valueStr}", Limpo: "${cleanValue}", Valor: ${value}`);
          
          if (!isNaN(value)) {
            contasAPagarTotal += value;
            console.log(`🔍 [PDF] Contas a Pagar total atualizado: ${contasAPagarTotal}`);
          }
        } else if (row[0] === 'Contas a Receber' && row[4]) {
          // Extrair valor da string formatada (ex: "R$ 109,89")
          const valueStr = row[4].toString();
          const cleanValue = valueStr.replace(/[^\d,.-]/g, '').replace(',', '.');
          const value = parseFloat(cleanValue);
          
          console.log(`🔍 [PDF] Contas a Receber - Original: "${valueStr}", Limpo: "${cleanValue}", Valor: ${value}`);
          
          if (!isNaN(value)) {
            contasAReceberTotal += value;
            console.log(`🔍 [PDF] Contas a Receber total atualizado: ${contasAReceberTotal}`);
          }
        }
      });
      
      console.log(`🔍 [PDF] Totais finais - Contas a Pagar: ${contasAPagarTotal}, Contas a Receber: ${contasAReceberTotal}`);
    }
    
    const total = contasAPagarTotal + contasAReceberTotal;
    if (total === 0) return currentY - 80;
    
    // Desenhar título do gráfico
    page.drawText('Distribuição Financeira', {
      x: chartCenterX - 80,
      y: currentY,
      size: 12,
      font: boldFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    
    // Calcular porcentagens
    const contasAReceberPercent = (contasAReceberTotal / total) * 100;
    const contasAPagarPercent = (contasAPagarTotal / total) * 100;
    
    // Desenhar gráfico de pizza simplificado
    this.drawSimplifiedPieChart(page, chartCenterX, chartCenterY, chartRadius, contasAReceberTotal, contasAPagarTotal, total);
    
    // Desenhar valores no centro do círculo
    const contasAReceberText = `R$ ${contasAReceberTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    const contasAPagarText = `R$ ${contasAPagarTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    const saldoText = `Saldo: R$ ${(contasAReceberTotal - contasAPagarTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    
    // Texto centralizado no círculo
    page.drawText('RESUMO', {
      x: chartCenterX - 25,
      y: chartCenterY + 15,
      size: 8,
      font: boldFont,
      color: rgb(0.1, 0.1, 0.1),
    });
    
    page.drawText(`Entrada: ${contasAReceberText}`, {
      x: chartCenterX - 40,
      y: chartCenterY + 2,
      size: 7,
      font: font,
      color: rgb(0.2, 0.6, 0.2),
    });
    
    page.drawText(`Saída: ${contasAPagarText}`, {
      x: chartCenterX - 35,
      y: chartCenterY - 10,
      size: 7,
      font: font,
      color: rgb(0.8, 0.2, 0.2),
    });
    
    const saldoColor = (contasAReceberTotal - contasAPagarTotal) >= 0 ? rgb(0.2, 0.6, 0.2) : rgb(0.8, 0.2, 0.2);
    page.drawText(saldoText, {
      x: chartCenterX - 45,
      y: chartCenterY - 22,
      size: 7,
      font: boldFont,
      color: saldoColor,
    });
    
    // Legenda detalhada
    const legendY = chartCenterY - chartRadius - 50;
    const legendX = chartCenterX - 80;
    
    // Contas a Receber
    page.drawRectangle({
      x: legendX,
      y: legendY,
      width: 12,
      height: 12,
      color: rgb(0.2, 0.6, 0.2),
    });
    page.drawText(`Contas a Receber (${contasAReceberPercent.toFixed(1)}%)`, {
      x: legendX + 18,
      y: legendY + 2,
      size: 8,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Contas a Pagar
    page.drawRectangle({
      x: legendX,
      y: legendY - 18,
      width: 12,
      height: 12,
      color: rgb(0.8, 0.2, 0.2),
    });
    page.drawText(`Contas a Pagar (${contasAPagarPercent.toFixed(1)}%)`, {
      x: legendX + 18,
      y: legendY - 16,
      size: 8,
      font: font,
      color: rgb(0, 0, 0),
    });
    
    // Desenhar linha divisória
    page.drawLine({
      start: { x: margin, y: legendY - 35 },
      end: { x: pageWidth - margin, y: legendY - 35 },
      thickness: 0.5,
      color: rgb(0.7, 0.7, 0.7),
    });
    
    return legendY - 45;
  }

  // Método simplificado para desenhar gráfico visual usando barras e círculo
  private drawSimplifiedPieChart(page: any, centerX: number, centerY: number, radius: number, contasAReceberTotal: number, contasAPagarTotal: number, total: number): void {
    // Desenhar círculo base vazio
    page.drawCircle({
      x: centerX,
      y: centerY,
      size: radius,
      borderColor: rgb(0.3, 0.3, 0.3),
      borderWidth: 2,
    });
    
    // Desenhar representação visual com barras concêntricas
    const barWidth = radius * 2;
    const barHeight = 12;
    const barY = centerY - radius - 20;
    
    // Fundo da barra
    page.drawRectangle({
      x: centerX - radius,
      y: barY,
      width: barWidth,
      height: barHeight,
      color: rgb(0.95, 0.95, 0.95),
      borderColor: rgb(0.7, 0.7, 0.7),
      borderWidth: 1,
    });
    
    // Barra verde para contas a receber
    if (contasAReceberTotal > 0) {
      const greenBarWidth = (contasAReceberTotal / total) * barWidth;
      page.drawRectangle({
        x: centerX - radius,
        y: barY,
        width: greenBarWidth,
        height: barHeight,
        color: rgb(0.2, 0.6, 0.2),
      });
    }
    
    // Barra vermelha para contas a pagar
    if (contasAPagarTotal > 0) {
      const redBarWidth = (contasAPagarTotal / total) * barWidth;
      const redBarX = centerX - radius + (contasAReceberTotal / total) * barWidth;
      page.drawRectangle({
        x: redBarX,
        y: barY,
        width: redBarWidth,
        height: barHeight,
        color: rgb(0.8, 0.2, 0.2),
      });
    }
    
    // Desenhar linhas divisórias visuais no círculo
    if (contasAReceberTotal > 0 && contasAPagarTotal > 0) {
      const angle = (contasAReceberTotal / total) * 2 * Math.PI - Math.PI / 2;
      const lineEndX = centerX + radius * Math.cos(angle);
      const lineEndY = centerY + radius * Math.sin(angle);
      
      // Linha divisória
      page.drawLine({
        start: { x: centerX, y: centerY },
        end: { x: lineEndX, y: lineEndY },
        thickness: 2,
        color: rgb(0.2, 0.2, 0.2),
      });
      
      // Linha vertical (12h)
      page.drawLine({
        start: { x: centerX, y: centerY },
        end: { x: centerX, y: centerY - radius },
        thickness: 2,
        color: rgb(0.2, 0.2, 0.2),
      });
    }
    
    // Adicionar indicadores visuais de setor
    const contasAReceberPercent = (contasAReceberTotal / total) * 100;
    const contasAPagarPercent = (contasAPagarTotal / total) * 100;
    
    // Texto indicativo no círculo (removido para evitar problemas de fonte)
  }
}
